import { RootState } from "app/store";
import { useStrings } from "features/i18n/useStrings";
import { useState } from "react";
import { shallowEqual } from "react-redux";
import {
    GuxButton,
    GuxFormFieldSearch,
    GuxIcon,
    GuxModal,
    GuxPagination,
    GuxTable,
    GuxTooltip,
    GuxTruncate,
    GuxButtonSlot,
} from "genesys-spark-components-react";
import { DeleteButton } from "features/common/ActionIcons";
import strings from "./ManageTemplate.strings";
import "./ManageTemplate.less";
import {
    handleDeleteTemplate,
    selectFavouriteTemplateIds,
    selectIsTemplateIdFavorite,
    setFavoriteTemplate,
    unsetFavouriteTemplate,
} from "features/templates/templateSlice";
import {
    selectFormType,
    selectIsSurveyOrEvaluationForm,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import FormType from "enums/FormType";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { ComposerTemplate } from "@genesys/cloud-frontend/dist/mjs/models/ComposerTemplate";
import { isNotNull } from "features/scorable/scorableSlice";

export enum TemplateOperation {
    add = "add",
    manage = "manage",
}

export function ManageTemplate() {
    const t = useStrings(strings);
    const [isTemplateManagerOpen, setIsTemplateManagerOpen] = useState(false);
    const hasTemplateSupport = useAppSelector((state) => {
        return selectFormType(state) === FormType.EVALUATION;
    });
    const isSurveyOrEvaluation = useAppSelector(selectIsSurveyOrEvaluationForm);

    return (
        <div className="ManageTemplate">
            {hasTemplateSupport && (
                <div className="manage-template-button">
                    <GuxButton
                        {...(isSurveyOrEvaluation && { accent: "ghost" })}
                        onClick={() => setIsTemplateManagerOpen(true)}
                    >
                        <span>{t.labels.title.manage}</span>
                    </GuxButton>
                </div>
            )}
            {isTemplateManagerOpen && (
                <TemplateController
                    isTemplateManagerOpen={isTemplateManagerOpen}
                    close={() => setIsTemplateManagerOpen(false)}
                />
            )}
        </div>
    );
}

export function TemplateController({
    isTemplateManagerOpen,
    close,
    templateOperation,
    addTemplate,
}: {
    isTemplateManagerOpen: boolean;
    close: () => void;
    templateOperation: TemplateOperation;
    addTemplate: (templateId: string) => void;
}) {
    const t = useStrings(strings);

    const [searchValue, setSearchValue] = useState<string>("");
    const onSearch = (keyword: string) => {
        setSearchValue(keyword);
    };

    return (
        <div className="TemplateController">
            <GuxModal
                open={isTemplateManagerOpen}
                size="medium"
                onGuxdismiss={() => close()}
                data-testid="templateController"
            >
                <div slot="title" className="gux-heading-lg-bold">
                    {t.labels.title[templateOperation]}
                </div>
                <div slot="content" className="content-container">
                    <div className="search">
                        <GuxFormFieldSearch
                            labelPosition="screenreader"
                            onInput={(e) => {
                                onSearch(
                                    (e.target as HTMLTextAreaElement).value
                                );
                            }}
                        >
                            <input
                                slot="input"
                                type="search"
                                placeholder={t.labels.search}
                            />
                            <label slot="label">{t.labels.search}</label>
                        </GuxFormFieldSearch>
                    </div>
                    <TemplateTable
                        templateOperation={templateOperation}
                        searchValue={searchValue}
                        onAdd={addTemplate}
                    />
                </div>
                {templateOperation === TemplateOperation.manage && (
                    <div slot="end-align-buttons">
                        <GuxButtonSlot accent="primary">
                            <button type="button" onClick={() => close()}>
                                {t.labels.done}
                            </button>
                        </GuxButtonSlot>
                    </div>
                )}
            </GuxModal>
        </div>
    );
}

TemplateController.defaultProps = {
    templateOperation: TemplateOperation.manage,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    addTemplate: () => {},
};

function TemplateTable({
    searchValue,
    templateOperation,
    onAdd,
}: {
    searchValue: string;
    templateOperation: TemplateOperation;
    onAdd: (templateId: string) => void;
}) {
    const dispatch = useAppDispatch();
    const t = useStrings(strings);
    const pageSize = 25;
    const [pageNumber, setPageNumber] = useState(1);
    const isTemplateAddingView = templateOperation === TemplateOperation.add;

    // updates on page change + on search
    const favouriteTemplateIds = useAppSelector(selectFavouriteTemplateIds);
    const searchedTemplatesInPage: Array<ComposerTemplate> = useAppSelector(
        (state) => {
            return Object.values(state.template.data.templates ?? {})
                .filter(isNotNull)
                .filter((template) => template.name?.includes(searchValue))
                .slice(pageNumber * pageSize - pageSize, pageNumber * pageSize)
                .sort((a, b) => {
                    return isTemplateAddingView &&
                        favouriteTemplateIds.includes(b.id ?? "") &&
                        !favouriteTemplateIds.includes(a.id ?? "")
                        ? 1
                        : -1;
                });
        },
        shallowEqual
    );

    const templateSize = useAppSelector(
        (state) => Object.keys(state.template.data.templates ?? {}).length
    );

    return (
        <div className="TemplateTable">
            <div className="table-placeholder">
                <GuxTable object-table>
                    <table
                        slot="data"
                        className="template-table"
                        data-testid="template-table"
                    >
                        <thead>
                            <caption>{t.labels.templateName}</caption>
                        </thead>
                        <tbody>
                            {searchedTemplatesInPage?.map((template) => {
                                return (
                                    <TemplateRow
                                        onAdd={onAdd}
                                        templateId={template.id!}
                                        onDelete={() => {
                                            dispatch(
                                                handleDeleteTemplate(
                                                    template.id!
                                                )
                                            );
                                        }}
                                        key={template.id!}
                                        templateOperation={templateOperation}
                                    />
                                );
                            })}
                        </tbody>
                    </table>
                </GuxTable>
            </div>
            <GuxPagination
                totalItems={templateSize ?? 0}
                itemsPerPage={pageSize}
                onGuxpaginationchange={(e) => {
                    setPageNumber(e.detail.currentPage);
                }}
                layout="simple"
                data-testid="templates-pagination"
            />
        </div>
    );
}
function TemplateRow({
    templateId,
    onDelete,
    onAdd,
    templateOperation,
}: {
    templateId: string;
    onDelete: () => void;
    onAdd: (templateId: string) => void;
    templateOperation: TemplateOperation;
}) {
    const isTemplateAddingView = templateOperation === TemplateOperation.add;
    const isTemplateManagedView =
        templateOperation === TemplateOperation.manage;
    const className = `${isTemplateAddingView ? "Clickable" : ""}`;

    const templateInfo = useAppSelector((state: RootState) => {
        const template = state.template.data.templates?.[templateId] ?? {};
        return { name: template.name ?? "" };
    }, shallowEqual);

    return (
        <>
            <tr
                className={className}
                tabIndex={isTemplateAddingView ? 0 : -1}
                onClick={() => (isTemplateAddingView ? onAdd(templateId) : {})}
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        return isTemplateAddingView ? onAdd(templateId) : {};
                    }
                }}
            >
                <td>
                    <div className="manage-template-cell">
                        <FavouriteTemplate
                            templateId={templateId}
                            templateOperation={templateOperation}
                        />
                        <div className="template-name">
                            <GuxTruncate maxLines={1}>
                                {templateInfo.name}
                            </GuxTruncate>
                        </div>
                        {isTemplateManagedView && (
                            <DeleteButton onDelete={onDelete} />
                        )}
                    </div>
                </td>
            </tr>
        </>
    );
}

function FavouriteTemplate({
    templateId,
    templateOperation,
}: {
    templateId: string;
    templateOperation: TemplateOperation;
}) {
    const dispatch = useAppDispatch();
    const t = useStrings(strings);
    const isTemplateFavourite = useAppSelector((state) =>
        selectIsTemplateIdFavorite(state, templateId)
    );
    const isTemplateManagedView =
        templateOperation === TemplateOperation.manage;
    const isTemplateAddingView = templateOperation === TemplateOperation.add;
    const className = `FavouriteTemplate ${
        isTemplateManagedView ? "Clickable" : ""
    }`;
    return (
        <div className={className}>
            <GuxIcon
                iconName={isTemplateFavourite ? "rating-active" : "rating"}
                screenreader-text={t.labels.favourite}
                decorative={isTemplateAddingView}
                onClick={() =>
                    isTemplateManagedView &&
                    dispatch(
                        isTemplateFavourite
                            ? unsetFavouriteTemplate({
                                  templateId,
                              })
                            : setFavoriteTemplate({
                                  templateId,
                              })
                    )
                }
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        isTemplateManagedView &&
                            dispatch(
                                isTemplateFavourite
                                    ? unsetFavouriteTemplate({
                                          templateId,
                                      })
                                    : setFavoriteTemplate({
                                          templateId,
                                      })
                            );
                    }
                }}
                tabIndex={isTemplateManagedView ? 0 : -1}
            />
            {isTemplateManagedView && (
                <GuxTooltip>
                    {isTemplateFavourite
                        ? t.labels.removeFavourite
                        : t.labels.addFavourite}
                </GuxTooltip>
            )}
        </div>
    );
}
